/**
 * FSRS-5 简化实现 — 概念级间隔重复调度
 *
 * 每个 concept key（如 "preflop:BTN:3bet"）维护独立的记忆状态。
 * 答对 → stability 增长 → 下次复习间隔拉长；答错 → stability 衰减 → 间隔缩短。
 * 纯客户端 localStorage，无服务端依赖。
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export type FsrsCard = {
  key: string;
  stability: number;
  difficulty: number;
  reps: number;
  lapses: number;
  last_review: number;
  next_review: number;
};

export type FsrsGrade = "again" | "hard" | "good" | "easy";

// ─── Constants (FSRS-5 defaults) ────────────────────────────────────────────

const W = [0.4, 0.6, 2.4, 5.8, 4.93, 0.94, 0.86, 0.01, 1.49, 0.14, 0.94, 2.18, 0.05, 0.34, 1.26, 0.29, 2.61];
const DECAY = -0.5;
const FACTOR = 19 / 81;
const REQUEST_RETENTION = 0.9;

// ─── Core FSRS Math ──────────────────────────────────────────────────────────

function initStability(grade: FsrsGrade): number {
  const idx = { again: 0, hard: 1, good: 2, easy: 3 }[grade];
  return Math.max(W[idx], 0.1);
}

function initDifficulty(grade: FsrsGrade): number {
  const g = { again: 4, hard: 3, good: 2, easy: 1 }[grade];
  return clampDifficulty(W[4] - (g - 3) * W[5]);
}

function clampDifficulty(d: number): number {
  return Math.min(Math.max(d, 1), 10);
}

function nextDifficulty(d: number, grade: FsrsGrade): number {
  const g = { again: 4, hard: 3, good: 2, easy: 1 }[grade];
  const newD = d - W[6] * (g - 3);
  const meanReversion = W[7] * (initDifficulty("good") - newD);
  return clampDifficulty(newD + meanReversion);
}

function nextRecallStability(s: number, d: number, r: number, grade: FsrsGrade): number {
  const hardPenalty = grade === "hard" ? W[15] : 1;
  const easyBonus = grade === "easy" ? W[16] : 1;
  return (
    s *
    (1 +
      Math.exp(W[8]) *
        (11 - d) *
        Math.pow(s, -W[9]) *
        (Math.exp((1 - r) * W[10]) - 1) *
        hardPenalty *
        easyBonus)
  );
}

function nextForgetStability(s: number, d: number, r: number): number {
  return (
    W[11] *
    Math.pow(d, -W[12]) *
    (Math.pow(s + 1, W[13]) - 1) *
    Math.exp((1 - r) * W[14])
  );
}

function retrievability(s: number, elapsedDays: number): number {
  if (s <= 0) return 0;
  return Math.pow(1 + FACTOR * (elapsedDays / s), DECAY);
}

function nextInterval(s: number): number {
  const interval = (s / FACTOR) * (Math.pow(REQUEST_RETENTION, 1 / DECAY) - 1);
  return Math.max(1, Math.round(interval));
}

// ─── Scheduler ───────────────────────────────────────────────────────────────

export function reviewCard(card: FsrsCard | null, key: string, grade: FsrsGrade, now: number): FsrsCard {
  if (!card) {
    const s = initStability(grade);
    const d = initDifficulty(grade);
    const interval = nextInterval(s);
    return {
      key,
      stability: s,
      difficulty: d,
      reps: 1,
      lapses: grade === "again" ? 1 : 0,
      last_review: now,
      next_review: now + interval * 86_400_000,
    };
  }

  const elapsedDays = Math.max(0, (now - card.last_review) / 86_400_000);
  const r = retrievability(card.stability, elapsedDays);
  const d = nextDifficulty(card.difficulty, grade);

  let s: number;
  let lapses = card.lapses;
  if (grade === "again") {
    s = nextForgetStability(card.stability, d, r);
    lapses += 1;
  } else {
    s = nextRecallStability(card.stability, d, r, grade);
  }

  const interval = nextInterval(s);
  return {
    key,
    stability: s,
    difficulty: d,
    reps: card.reps + 1,
    lapses,
    last_review: now,
    next_review: now + interval * 86_400_000,
  };
}

// ─── Storage ─────────────────────────────────────────────────────────────────

const FSRS_KEY = "gto-fsrs-v1";
const FSRS_EVENT = "gto-fsrs";

type FsrsStore = Record<string, FsrsCard>;

export function loadFsrsStore(): FsrsStore {
  try {
    const raw = localStorage.getItem(FSRS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as FsrsStore;
  } catch {
    return {};
  }
}

function saveFsrsStore(store: FsrsStore): void {
  localStorage.setItem(FSRS_KEY, JSON.stringify(store));
  fsrsRaw = null; // 强制下次快照刷新(照抄 train-log 范式)
  window.dispatchEvent(new Event(FSRS_EVENT));
}

// ─── useSyncExternalStore 适配(缓存快照,引用稳定;供 stats 等页 hydration 安全读取) ──

let fsrsCache: FsrsStore = {};
let fsrsRaw: string | null = null;

export function getFsrsSnapshot(): FsrsStore {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(FSRS_KEY);
  } catch {
    raw = null;
  }
  if (raw !== fsrsRaw) {
    fsrsRaw = raw;
    fsrsCache = loadFsrsStore();
  }
  return fsrsCache;
}

export function subscribeFsrs(onChange: () => void): () => void {
  window.addEventListener("storage", onChange);
  window.addEventListener(FSRS_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(FSRS_EVENT, onChange);
  };
}

/** 只读单卡;不存在返回 null。不改 store。 */
export function getFsrsCard(key: string): FsrsCard | null {
  const store = loadFsrsStore();
  return store[key] ?? null;
}

export function reviewConcept(key: string, grade: FsrsGrade): FsrsCard {
  const store = loadFsrsStore();
  const now = Date.now();
  const updated = reviewCard(store[key] ?? null, key, grade, now);
  store[key] = updated;
  saveFsrsStore(store);
  return updated;
}

export function getDueConcepts(limit = 20): FsrsCard[] {
  const store = loadFsrsStore();
  const now = Date.now();
  return Object.values(store)
    .filter((c) => c.next_review <= now)
    .sort((a, b) => a.next_review - b.next_review)
    .slice(0, limit);
}

export function getConceptStats(): { total: number; due: number; learned: number } {
  const store = loadFsrsStore();
  const now = Date.now();
  const cards = Object.values(store);
  return {
    total: cards.length,
    due: cards.filter((c) => c.next_review <= now).length,
    learned: cards.filter((c) => c.reps >= 3 && c.stability > 7).length,
  };
}

// ─── Concept Key Builder ─────────────────────────────────────────────────────

import { normalizePosition } from "./normalize-position";

export function buildConceptKey(scene: {
  spot: { street: string; hero_position: string };
  concepts?: { scenarios?: string[] };
}): string {
  const street = scene.spot.street;
  const pos = normalizePosition(scene.spot.hero_position);
  const scenario = scene.concepts?.scenarios?.[0] ?? "general";
  return `${street}:${pos}:${scenario}`;
}

